import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useBuildFromBonesStore, type DeconstructionStep } from '@/store/build-from-bones-store';
import { useAudioEngine } from '@/hooks/useAudioEngine';
import { generateSATBVoicing } from '@/audio/VoiceLeading';
import { useProgressionsStore } from '@/store/progressions-store';
import type { Chord, Voices } from '@/types/chord';
import styles from './BuildFromBonesPanel.module.css';

/** Default tempo used for Build From Bones playback (BPM) */
const DEFAULT_BPM = 100;

/**
 * Convert a step's chord list to an array of note arrays via SATB voicing.
 * Uses voice leading between successive chords when available.
 */
function chordsToNoteArrays(chords: Chord[]): string[][] {
  const noteArrays: string[][] = [];
  let previousVoicing: Voices | undefined;
  for (const chord of chords) {
    const voicing = generateSATBVoicing(chord, previousVoicing);
    noteArrays.push([voicing.bass, voicing.tenor, voicing.alto, voicing.soprano]);
    previousVoicing = voicing;
  }
  return noteArrays;
}

/**
 * Schedule a chord sequence via setTimeout.
 * Returns the array of timeout IDs so they can be cancelled.
 * @param noteArrays   - Array of note arrays (one per chord)
 * @param chordDurSec  - Duration of each chord in seconds
 * @param gapMs        - Optional leading gap in ms (for compare mode)
 * @param playChord    - audioEngine.playChord wrapper
 * @param onDone       - Called when the final chord finishes
 */
function scheduleChordSequence(
  noteArrays: string[][],
  chordDurSec: number,
  gapMs: number,
  playChord: (notes: string[], duration?: number) => void,
  onDone?: () => void,
): ReturnType<typeof setTimeout>[] {
  const timeoutIds: ReturnType<typeof setTimeout>[] = [];
  noteArrays.forEach((notes, idx) => {
    const delayMs = gapMs + idx * chordDurSec * 1000;
    const id = setTimeout(() => {
      playChord(notes, chordDurSec);
    }, delayMs);
    timeoutIds.push(id);
  });
  if (onDone) {
    const totalMs = gapMs + noteArrays.length * chordDurSec * 1000;
    const doneId = setTimeout(onDone, totalMs);
    timeoutIds.push(doneId);
  }
  return timeoutIds;
}

/**
 * Helper component: StepIndicator
 * Displays clickable dots and progress line for navigating between steps
 */
interface StepIndicatorProps {
  steps: DeconstructionStep[];
  currentStep: number;
  onStepClick: (stepIndex: number) => void;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ steps, currentStep, onStepClick }) => {
  return (
    <div className={styles.stepIndicator}>
      {steps.map((step, index) => {
        const isActive = index === currentStep;
        const isCompleted = index < currentStep;

        return (
          <React.Fragment key={step.stepNumber}>
            {/* Step dot with geometric frame */}
            <div className={styles.stepContainer}>
              <button
                className={`${styles.stepDot} ${isActive ? styles.active : ''} ${isCompleted ? styles.completed : ''}`}
                onClick={() => onStepClick(index)}
                title={`Go to Step ${index + 1}: ${step.stepName}`}
                aria-label={`Step ${index + 1}: ${step.stepName}`}
                aria-current={isActive ? 'step' : undefined}
              >
                <span className={styles.stepNumber}>{index + 1}</span>
                {isCompleted && (
                  <span className={styles.checkmark} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M3 8.5L6.5 12L13 4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                )}
              </button>
              <span className={styles.stepLabel}>{step.stepName}</span>
            </div>

            {/* Progress line between dots (skip after last step) - wobbly SVG path */}
            {index < steps.length - 1 && (
              <svg
                className={`${styles.progressLine} ${isCompleted ? styles.completed : ''}`}
                width="32"
                height="24"
                viewBox="0 0 32 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  className={styles.progressPath}
                  d="M0 12 C8 8, 12 16, 16 12 C20 8, 24 16, 32 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/**
 * Layer type display info - icons and colors for each layer
 */
const LAYER_INFO: Record<string, { icon: string; color: string; label: string }> = {
  skeleton: { icon: '🎵', color: '#888', label: 'Foundation' },
  sevenths: { icon: '7', color: '#E8A03E', label: 'Richness' },
  suspensions: { icon: '~', color: '#6B8FAD', label: 'Tension' },
  extensions: { icon: '↑', color: '#7A9E87', label: 'Color' },
  alterations: { icon: '♯♭', color: '#C4756E', label: 'Edge' },
};

/**
 * Helper component: StepContent
 * Displays the current step's name, description, and progression preview
 * Now with enhanced display for layerType, modifiedIndices, and romanNumerals
 */
interface StepContentProps {
  step: DeconstructionStep;
}

const StepContent: React.FC<StepContentProps> = ({ step }) => {
  // Use backend-provided Roman numerals if available, otherwise generate from chords
  const progressionText = useMemo(() => {
    if (step.romanNumerals) {
      return step.romanNumerals;
    }
    // Fallback to generating from chords
    const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];
    return step.chords
      .map((chord) => {
        const numeral = romanNumerals[chord.scaleDegree - 1] || 'I';
        if (chord.quality === 'minor' || chord.quality === 'min7') {
          return numeral.toLowerCase();
        }
        if (chord.quality === 'dom7') return `${numeral}7`;
        if (chord.quality === 'maj7') return `${numeral}maj7`;
        if (chord.quality === 'diminished') return `${numeral}°`;
        if (chord.quality === 'augmented') return `${numeral}+`;
        return numeral;
      })
      .join(' → ');
  }, [step.romanNumerals, step.chords]);

  // Get layer display info
  const layerInfo = LAYER_INFO[step.layerType || 'skeleton'];
  const modifiedCount = step.modifiedIndices?.length || 0;

  return (
    <div className={styles.stepContent}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
      >
        {/* Step header with layer badge */}
        <div className={styles.stepHeader}>
          <h3 className={styles.stepTitle}>{step.stepName}</h3>
          {step.layerType && layerInfo && (
            <span
              className={styles.layerBadge}
              style={{ backgroundColor: layerInfo.color }}
              title={`Layer: ${layerInfo.label}`}
            >
              <span className={styles.layerIcon}>{layerInfo.icon}</span>
              <span className={styles.layerLabel}>{layerInfo.label}</span>
            </span>
          )}
        </div>

        {/* What changed indicator */}
        {modifiedCount > 0 && (
          <p className={styles.modifiedIndicator}>
            {modifiedCount} chord{modifiedCount !== 1 ? 's' : ''} modified from previous step
          </p>
        )}

        {/* AI-generated description */}
        <p className={styles.stepDescription}>{step.description}</p>

        {/* Progression preview with Roman numerals */}
        <div className={styles.progressionSection}>
          <p className={styles.progressionLabel}>Progression:</p>
          <div className={styles.progressionChords}>{progressionText}</div>
        </div>
      </motion.div>
    </div>
  );
};

/**
 * Helper component: NavigationControls
 * Previous, Play This Step, and Next buttons
 */
interface NavigationControlsProps {
  onPrevious: () => void;
  onPlayStep: () => void;
  onNext: () => void;
  isPlayingStep: boolean;
  canNavigate: boolean;
}

const NavigationControls: React.FC<NavigationControlsProps> = ({
  onPrevious,
  onPlayStep,
  onNext,
  isPlayingStep,
  canNavigate,
}) => {
  return (
    <div className={styles.navControls}>
      <button
        className={styles.navButton}
        onClick={onPrevious}
        disabled={!canNavigate}
        title="Previous step (Left arrow)"
      >
        ◄ Previous
      </button>
      <button
        className={`${styles.playButton} ${styles.primary}`}
        onClick={onPlayStep}
        title="Play this step"
      >
        <span className={styles.playIcon}>{isPlayingStep ? '■' : '▶'}</span>
        Play This Step
      </button>
      <button
        className={styles.navButton}
        onClick={onNext}
        disabled={!canNavigate}
        title="Next step (Right arrow)"
      >
        Next ►
      </button>
    </div>
  );
};

/**
 * Helper component: GlobalControls
 * Play sequence, compare, and save buttons
 */
interface GlobalControlsProps {
  onPlaySequence: () => void;
  onCompare: () => void;
  onSave: () => void;
  isPlayingSequence: boolean;
  isComparing: boolean;
}

const GlobalControls: React.FC<GlobalControlsProps> = ({
  onPlaySequence,
  onCompare,
  onSave,
  isPlayingSequence,
  isComparing,
}) => {
  return (
    <div className={styles.globalControls}>
      <button
        className={`${styles.playButton} ${styles.primary}`}
        onClick={onPlaySequence}
        title="Play all steps in sequence (10 seconds each)"
      >
        <span className={styles.playIcon}>{isPlayingSequence ? '■' : '▶'}</span>
        Play All Steps In Sequence
      </button>
      <button
        className={`${styles.playButton} ${styles.primary}`}
        onClick={onCompare}
        title="Compare step 0 vs final step"
      >
        <span className={styles.playIcon}>{isComparing ? '■' : '▶'}</span>
        Compare Step 0 vs Final
      </button>
      <button
        className={`${styles.playButton} ${styles.secondary}`}
        onClick={onSave}
        title="Save this build-up to My Progressions"
      >
        Save This Build-Up
      </button>
    </div>
  );
};

/**
 * BuildFromBonesPanel Component
 * Main panel component that displays step-by-step deconstruction of progressions
 * Slides up from bottom with framer-motion AnimatePresence
 * Includes inline sub-components: StepIndicator, StepContent, NavigationControls, GlobalControls
 */
export const BuildFromBonesPanel: React.FC = () => {
  const {
    isPanelOpen,
    currentStep,
    totalSteps,
    steps,
    isPlaying,
    playbackMode,
    isLoading,
    error,
    closePanel,
    nextStep,
    prevStep,
    jumpToStep,
    setPlaying,
    setPlaybackMode,
    setError,
  } = useBuildFromBonesStore();

  // Audio engine hook — provides playChord, stopAll, etc.
  const { playChord, stopAll } = useAudioEngine();

  // Ref that holds outstanding setTimeout IDs so we can cancel them on stop
  const pendingTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  /** Cancel all in-flight playback timeouts and stop Tone.js notes */
  const cancelPlayback = useCallback(() => {
    pendingTimeoutsRef.current.forEach(clearTimeout);
    pendingTimeoutsRef.current = [];
    stopAll();
  }, [stopAll]);

  // Track which playback mode is currently active
  const isPlayingStep = isPlaying && playbackMode === 'single';
  const isPlayingSequence = isPlaying && playbackMode === 'sequence';
  const isComparing = isPlaying && playbackMode === 'compare';

  // Cancel playback when the panel closes
  useEffect(() => {
    if (!isPanelOpen) {
      cancelPlayback();
    }
  }, [isPanelOpen, cancelPlayback]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isPanelOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closePanel();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        nextStep();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevStep();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPanelOpen, closePanel, nextStep, prevStep]);

  // Chord duration in seconds at DEFAULT_BPM (one beat per chord)
  const chordDurSec = (60 / DEFAULT_BPM) * 2; // 2 beats per chord

  /**
   * Play This Step — plays the current step's chord progression once.
   * Toggles stop if already playing.
   */
  const handlePlayStep = useCallback(() => {
    if (isPlayingStep) {
      cancelPlayback();
      setPlaying(false);
      setPlaybackMode(null);
      return;
    }
    cancelPlayback();

    const stepData = steps[currentStep];
    if (!stepData || stepData.chords.length === 0) return;

    setPlaying(true);
    setPlaybackMode('single');

    const noteArrays = chordsToNoteArrays(stepData.chords);
    const ids = scheduleChordSequence(noteArrays, chordDurSec, 0, playChord, () => {
      setPlaying(false);
      setPlaybackMode(null);
    });
    pendingTimeoutsRef.current = ids;
  }, [isPlayingStep, steps, currentStep, chordDurSec, playChord, cancelPlayback, setPlaying, setPlaybackMode]);

  /**
   * Play All Steps In Sequence — plays every step one after another,
   * with a 1-second gap between steps.
   * Toggles stop if already playing.
   */
  const handlePlaySequence = useCallback(() => {
    if (isPlayingSequence) {
      cancelPlayback();
      setPlaying(false);
      setPlaybackMode(null);
      return;
    }
    cancelPlayback();
    if (steps.length === 0) return;

    setPlaying(true);
    setPlaybackMode('sequence');

    const GAP_MS = 1000;
    const allIds: ReturnType<typeof setTimeout>[] = [];
    let cumulativeMs = 0;

    steps.forEach((step) => {
      if (step.chords.length === 0) return;
      const noteArrays = chordsToNoteArrays(step.chords);
      const stepDurMs = noteArrays.length * chordDurSec * 1000;

      const ids = scheduleChordSequence(noteArrays, chordDurSec, cumulativeMs, playChord);
      allIds.push(...ids);

      cumulativeMs += stepDurMs + GAP_MS;
    });

    // Mark done after all steps finish
    const doneId = setTimeout(() => {
      setPlaying(false);
      setPlaybackMode(null);
    }, cumulativeMs);
    allIds.push(doneId);

    pendingTimeoutsRef.current = allIds;
  }, [isPlayingSequence, steps, chordDurSec, playChord, cancelPlayback, setPlaying, setPlaybackMode]);

  /**
   * Compare Step 0 vs Final — plays the skeleton, pauses 1 second,
   * then plays the fully-decorated final step.
   * Toggles stop if already playing.
   */
  const handleCompare = useCallback(() => {
    if (isComparing) {
      cancelPlayback();
      setPlaying(false);
      setPlaybackMode(null);
      return;
    }
    cancelPlayback();
    if (steps.length < 2) return;

    setPlaying(true);
    setPlaybackMode('compare');

    const GAP_MS = 1000;
    const firstStep = steps[0];
    const lastStep = steps[steps.length - 1];

    if (!firstStep || !lastStep) return;

    const firstNoteArrays = chordsToNoteArrays(firstStep.chords);
    const lastNoteArrays = chordsToNoteArrays(lastStep.chords);
    const firstDurMs = firstNoteArrays.length * chordDurSec * 1000;

    const firstIds = scheduleChordSequence(firstNoteArrays, chordDurSec, 0, playChord);
    const lastIds = scheduleChordSequence(lastNoteArrays, chordDurSec, firstDurMs + GAP_MS, playChord);

    const totalMs = firstDurMs + GAP_MS + lastNoteArrays.length * chordDurSec * 1000;
    const doneId = setTimeout(() => {
      setPlaying(false);
      setPlaybackMode(null);
    }, totalMs);

    pendingTimeoutsRef.current = [...firstIds, ...lastIds, doneId];
  }, [isComparing, steps, chordDurSec, playChord, cancelPlayback, setPlaying, setPlaybackMode]);

  /**
   * Save This Build-Up — saves each step as a separate progression titled
   * "Build From Bones: Step N — {stepName}" and closes the panel.
   */
  const handleSave = useCallback(async () => {
    if (steps.length === 0) return;

    const saveProgression = useProgressionsStore.getState().saveProgression;
    const now = new Date().toISOString();

    try {
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        await saveProgression({
          id: `bfb-${Date.now()}-step-${i}`,
          title: `Build From Bones: Step ${i + 1} — ${step.stepName}`,
          key: step.chords[0]?.key ?? 'C',
          mode: step.chords[0]?.mode ?? 'major',
          tempo: DEFAULT_BPM,
          timeSignature: '4/4',
          chords: step.chords,
          tags: ['build-from-bones'],
          isFavorite: false,
          createdAt: now,
          updatedAt: now,
        });
      }
      closePanel();
    } catch (err) {
      console.error('Failed to save Build From Bones steps:', err);
    }
  }, [steps, closePanel]);

  const canNavigate = totalSteps > 0 && !isPlaying;
  const currentStepData = steps[currentStep];

  const content = (
    <AnimatePresence>
      {isPanelOpen && (
        <>
          {/* Overlay backdrop */}
          <motion.div
            className={styles.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closePanel}
            aria-hidden="true"
          />

          {/* Panel */}
          <motion.div
            className={styles.panel}
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            role="complementary"
            aria-modal="false"
            aria-labelledby="build-from-bones-title"
          >
            {/* Header */}
            <div className={styles.header}>
              <h2 id="build-from-bones-title" className={styles.title}>
                Build from <i>bones</i>
              </h2>
              <button
                className={styles.closeButton}
                onClick={closePanel}
                aria-label="Close panel"
                title="Close (Esc)"
              >
                ✕
              </button>
            </div>

            {/* Step indicator with progress line */}
            {totalSteps > 0 && (
              <StepIndicator
                steps={steps}
                currentStep={currentStep}
                onStepClick={jumpToStep}
              />
            )}

            {/* Loading state */}
            {isLoading && (
              <div className={styles.loadingContainer}>
                <div className={styles.spinner} />
                <p className={styles.loadingMessage}>Deconstructing progression...</p>
              </div>
            )}

            {/* Error state */}
            {error && !isLoading && (
              <div className={styles.errorContainer}>
                <p className={styles.errorMessage}>{error}</p>
                <button
                  className={styles.retryButton}
                  onClick={() => setError(null)}
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* Main content area */}
            {!isLoading && !error && totalSteps > 0 && currentStepData ? (
              <>
                {/* Step content section */}
                <StepContent step={currentStepData} />

                {/* Navigation controls */}
                <NavigationControls
                  onPrevious={prevStep}
                  onPlayStep={handlePlayStep}
                  onNext={nextStep}
                  isPlayingStep={isPlayingStep}
                  canNavigate={canNavigate}
                />

                {/* Global playback controls */}
                <GlobalControls
                  onPlaySequence={handlePlaySequence}
                  onCompare={handleCompare}
                  onSave={handleSave}
                  isPlayingSequence={isPlayingSequence}
                  isComparing={isComparing}
                />
              </>
            ) : !isLoading && !error ? (
              /* Empty state */
              <div className={styles.emptyContainer}>
                <p className={styles.emptyMessage}>
                  Analyze a chord progression to see its step-by-step deconstruction.
                </p>
              </div>
            ) : null}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
};
